import { API_CONFIG } from '../../../config/api';

export interface Customer {
  CustomerID: string;
  CustomerName: string;
  EntId?: string;
  entId?: string;
  TinCode?: string;
  [key: string]: any;
}

export interface LoadCustomersResult {
  success: boolean;
  data: Customer[];
  error?: string;
}

/**
 * Customer-ийн жагсаалт авах функц
 * @param onComplete - Амжилттай дууссан үед дуудагдах callback функц
 * @param setWarningMessage - Warning message setter (optional)
 * @param setShowWarningAlert - Warning alert setter (optional)
 * @returns Promise<Customer[]>
 */
export const loadCustomers = async (
  onComplete?: () => void,
  setWarningMessage?: (message: string) => void,
  setShowWarningAlert?: (show: boolean) => void
): Promise<Customer[]> => {
  try {
    console.log('🔄 Loading customers list...');
    
    const accountantOid = localStorage.getItem('accountantOid');
    if (!accountantOid) {
      throw new Error('AccountantOid not found in localStorage');
    }
    
    // Backend route returns customers for current accountant from token
    const response = await fetch(`${API_CONFIG.BASE_URL}/api/customerslist/accountant/customers`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('novaqToken')}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        console.log('⚠️ Accountant not found (404), showing warning message');
        if (setWarningMessage && setShowWarningAlert) {
          setWarningMessage('Бүртгэлтэй харилцагч олдсонгүй');
          setShowWarningAlert(true);
        }
        return [];
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Handle null or undefined data
    if (data === null || data === undefined) {
      console.log('⚠️ API response data is null or undefined, showing warning message');
      if (setWarningMessage && setShowWarningAlert) {
        setWarningMessage('Бүртгэлтэй харилцагч олдсонгүй');
        setShowWarningAlert(true);
      }
      return [];
    }
    
    if (data.success) {
      // Check if data.data exists and is an array
      if (!data.data || !Array.isArray(data.data) || data.data.length === 0) {
        console.log('⚠️ No customers found, showing warning message');
        if (setWarningMessage && setShowWarningAlert) {
          setWarningMessage('Бүртгэлтэй харилцагч олдсонгүй');
          setShowWarningAlert(true);
        }
        return [];
      }
      
      console.log('✅ Customers loaded successfully:', data.data.length);
      
      console.log('🔍 Customer data structure:', data.data[0]);
      console.log('🔍 Available fields:', Object.keys(data.data[0]));
      
      // Log all EntIds from the customer list
      console.log('--- All Customer EntIds ---');
      const entIdList: string[] = [];
      data.data.forEach((customer: Customer, index: number) => {
        const entId = customer.EntId || customer.entId;
        const entIdValue = entId || 'NULL';
        entIdList.push(entIdValue);
        console.log(`Customer ${index + 1} (${customer.CustomerName}): EntId = ${entIdValue} (${customer.EntId ? 'from EntId' : customer.entId ? 'from entId' : 'NULL'})`);
      });
      console.log('📋 EntId жагсаалт:', entIdList);
      console.log('📊 Нийт EntId тоо:', entIdList.length);
      console.log('📊 NULL биш EntId тоо:', entIdList.filter(id => id !== 'NULL').length);
      console.log('-------------------------');
      
      // Call callback if provided
      if (onComplete) {
        onComplete();
      }
      
      return data.data;
    } else {
      console.log('⚠️ API returned success: false, showing warning message');
      if (setWarningMessage && setShowWarningAlert) {
        setWarningMessage('Бүртгэлтэй харилцагч олдсонгүй');
        setShowWarningAlert(true);
      }
      return [];
    }
  } catch (error) {
    console.error('❌ Error loading customers:', error);
    // 404 алдаа гарсан үед warning message харуулах
    if (setWarningMessage && setShowWarningAlert) {
      setWarningMessage('Бүртгэлтэй харилцагч олдсонгүй');
      setShowWarningAlert(true);
    }
    throw error;
  }
};

/**
 * Customer-ийн мэдээлэл авах функц (TaxGetHistory-д зориулсан)
 * @param setCustomers - Customer state setter
 * @param setSelectedCustomer - Selected customer state setter
 * @param setLoading - Loading state setter
 * @param setErrorMessage - Error message state setter
 * @param setShowErrorPopup - Show error popup state setter
 * @returns Promise<Customer[]>
 */
export const loadCustomersForTaxHistory = async (
  setCustomers: (customers: Customer[]) => void,
  setSelectedCustomer: (customer: Customer | null) => void,
  setLoading: (loading: boolean) => void,
  setErrorMessage: (message: string) => void,
  setShowErrorPopup: (show: boolean) => void,
  setWarningMessage?: (message: string) => void,
  setShowWarningAlert?: (show: boolean) => void
): Promise<Customer[]> => {
  try {
    console.log('🔄 Loading customers list...');
    setLoading(true);
    setErrorMessage('');
    
    const customers = await loadCustomers(undefined, setWarningMessage, setShowWarningAlert);
    
    // CustomerName-аар эрэмбэлэх
    const sortedCustomers = [...customers].sort((a, b) => {
      const nameA = (a.CustomerName || '').toLowerCase();
      const nameB = (b.CustomerName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    setCustomers(sortedCustomers);
    
    // Эхний customer-ийг сонгох
    if (sortedCustomers.length > 0) {
      setSelectedCustomer(sortedCustomers[0]);
      console.log('✅ First customer selected:', sortedCustomers[0].CustomerName);
    } else {
      // Харилцагчийн мэдээлэл байхгүй үед warning message харуулах
      console.log('⚠️ No customers found, showing warning message');
      if (setWarningMessage && setShowWarningAlert) {
        setWarningMessage('Бүртгэлтэй харилцагч олдсонгүй');
        setShowWarningAlert(true);
      }
    }
    
    return sortedCustomers;
  } catch (error) {
    console.error('❌ Error loading customers:', error);
    
    // 404 алдаа гарсан үед зөвхөн warning message харуулах, error message харуулахгүй
    if (setWarningMessage && setShowWarningAlert) {
      setWarningMessage('Бүртгэлтэй харилцагч олдсонгүй');
      setShowWarningAlert(true);
    } else {
      // Warning message setter байхгүй үед л error popup харуулах
      const errorMessage = 'Customer list авах алдаа: ' + (error as Error).message;
      setErrorMessage(errorMessage);
      setShowErrorPopup(true);
    }
    
    return [];
  } finally {
    setLoading(false);
  }
};

/**
 * Customer-ийн мэдээлэл авах функц (TaxGetList-д зориулсан)
 * @param setCustomers - Customer state setter
 * @param setLoading - Loading state setter
 * @param setError - Error state setter
 * @param onComplete - Callback функц
 * @returns Promise<Customer[]>
 */
export const loadCustomersForTaxList = async (
  setCustomers: (customers: Customer[]) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: string | null) => void,
  onComplete?: () => void
): Promise<Customer[]> => {
  try {
    console.log('🔄 Loading customers list...');
    setLoading(true);
    setError(null);
    
    const customers = await loadCustomers(onComplete);
    
    // CustomerName-аар эрэмбэлэх
    const sortedCustomers = [...customers].sort((a, b) => {
      const nameA = (a.CustomerName || '').toLowerCase();
      const nameB = (b.CustomerName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    setCustomers(sortedCustomers);
    
    return sortedCustomers;
  } catch (error) {
    console.error('❌ Error loading customers:', error);
    const errorMessage = 'Customer list авах алдаа: ' + (error as Error).message;
    setError(errorMessage);
    return [];
  } finally {
    setLoading(false);
  }
};
